/* mt-prov-sf protocol v1 (mt-prov-sf docs/protocol-v1.md), device API, over fetch. */

export class ProvError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export interface DeviceInfo {
  hostname: string;
  os: string;
  os_user: string;
  app_version: string;
  platform: "web";
}

export interface Credential {
  device_id: string;
  device_secret: string;
  endpoint_id?: string;
}

export interface SettingEntry {
  v: unknown;
  lock: boolean;
  hide: boolean;
  edit: boolean;
  src: string;
}

export interface ProvAccount {
  line_id: string;
  line: number;
  display_name: string;
  username: string;
  auth_userid: string;
  domain: string;
  realm: string;
  ha1?: string;
  auth?: "ephemeral";
  expires: number;
  /** the outside number the line's calls show (docs/protocol-v1.md) */
  caller_id?: string;
}

export interface ProvConfig {
  revision: string;
  device_id: string;
  poll_interval: number;
  directory_revision: string | null;
  settings: Record<string, SettingEntry>;
  accounts: ProvAccount[];
  /** present when the device has an address book (mt-prov-sf docs/contacts.md) */
  contacts_seq?: number;
  /** present when chat is on for the device (mt-prov-sf docs/chat.md) */
  chat_seq?: number;
  /** present when the shared call list is on (mt-prov-sf docs/calls.md) */
  calls_seq?: number;
  /** files can go with chat messages (with chat_seq) */
  chat_files?: boolean;
  /** other companies that opened extensions to this device's (docs/chat.md « Between companies ») */
  chat_partners?: ChatPartner[];
}

export interface ChatPartner {
  company: string;
  name: string;
  entries: { address: string; extension: string; name: string }[];
}

export interface ContactPhone {
  id: string;
  /** work, mobile, home, fax, other */
  type: string;
  number: string;
  ext?: string;
  label?: string;
}

export interface ContactEmail {
  id: string;
  type: string;
  address: string;
}

export interface ContactAddress {
  id: string;
  type: string;
  street?: string;
  city?: string;
  region?: string;
  postal_code?: string;
  country?: string;
}

/** A contact of the second design (mt-prov-sf docs/contacts.md "Contact") */
export interface BookContact {
  id: string;
  book: string;
  seq?: number;
  first_name?: string;
  last_name?: string;
  /** null: computed ("first last", else company, else the number) */
  display_name?: string | null;
  company?: string;
  title?: string;
  department?: string;
  phones?: ContactPhone[];
  primary_phone?: string | null;
  emails?: ContactEmail[];
  primary_email?: string | null;
  addresses?: ContactAddress[];
  tags?: string[];
  note?: string;
  favorite?: boolean;
  deleted?: boolean;
  /** contacts.list only: the name to show */
  name?: string;
}

export interface ContactBook {
  id: string;
  name: string;
  writable: boolean;
}

export interface ContactsPage {
  seq: number;
  full: boolean;
  more?: boolean;
  user?: string;
  books?: ContactBook[];
  contacts: BookContact[];
  /** per-device attributes of numbers (desk phones): not used here */
  overlays?: unknown[];
}

export interface ContactsPosted {
  seq: number;
  accepted: { id: string; seq: number; lost?: string[]; merged_into?: string }[];
  rejected: { id: string | null; code: string; message: string }[];
}

export interface Directory {
  revision: string;
  name: string;
  entries: { name: string; number: string }[];
}

export interface SipCredentials {
  expires_at: number;
  ttl: number;
  ice_servers?: RTCIceServer[];
  accounts: { line_id: string; auth_userid: string; password: string }[];
}

export interface IceServers {
  expires_at: number;
  ttl: number;
  ice_servers: RTCIceServer[];
}

export type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

export class ProvClient {
  constructor(
    readonly base: string,
    private fetchFn: Fetch = (u, i) => fetch(u, i),
  ) {
    this.base = base.replace(/\/+$/, "");
  }

  startPairing(device: DeviceInfo) {
    return this.json<{ pairing_id: string; code: string; expires_in: number; poll_interval: number }>(
      "POST",
      "/v1/pairing",
      { device },
    );
  }

  pollPairing(id: string) {
    return this.json<{ status: "pending" | "claimed"; expires_in?: number }>("GET", `/v1/pairing/${encodeURIComponent(id)}`);
  }

  enroll(by: { pairing_id: string } | { token: string }, device: DeviceInfo) {
    return this.json<Credential>("POST", "/v1/enroll", { ...by, device });
  }

  /** null on 304 */
  config(cred: Credential, etag?: string) {
    return this.json<ProvConfig | null>("GET", "/v1/config", undefined, cred, etag);
  }

  directory(cred: Credential, etag?: string) {
    return this.json<Directory | null>("GET", "/v1/directory", undefined, cred, etag);
  }

  patchSettings(cred: Credential, base: string | undefined, changes: unknown[]) {
    return this.json<{ revision: string; accepted: string[]; rejected: { key: string; code: string; effective: SettingEntry }[] }>(
      "PATCH",
      "/v1/settings",
      { base_revision: base, changes },
      cred,
    );
  }

  contacts(cred: Credential, since: number) {
    return this.json<ContactsPage>("GET", `/v1/contacts?since=${since}`, undefined, cred);
  }

  postContacts(cred: Credential, changes: unknown[]) {
    return this.json<ContactsPosted>("POST", "/v1/contacts/changes", { changes }, cred);
  }

  /** TURN credentials for this browser (mt-prov-sf GET /v1/ice-servers); 404 without a TURN server. */
  iceServers(cred: Credential) {
    return this.json<IceServers>("GET", "/v1/ice-servers", undefined, cred);
  }

  sipCredentials(cred: Credential) {
    return this.json<SipCredentials>("GET", "/v1/sip-credentials", undefined, cred);
  }

  /** A device-authenticated JSON call (voicemail, docs/voicemail.md of mt-prov-sf). */
  call<T>(method: string, path: string, cred: Credential, body?: unknown): Promise<T> {
    return this.json<T>(method, path, body, cred);
  }

  /** A device-authenticated call with a raw body (greeting upload) or a binary answer (audio). */
  async raw(method: string, path: string, cred: Credential, body?: BodyInit, contentType?: string): Promise<Response> {
    const headers: Record<string, string> = { Authorization: `Bearer ${cred.device_id}.${cred.device_secret}` };
    if (contentType) headers["Content-Type"] = contentType;
    let res: Response;
    try {
      res = await this.fetchFn(this.base + path, { method, headers, body, cache: "no-store", credentials: "omit" });
    } catch (e) {
      throw new ProvError(0, "unreachable", String(e));
    }
    if (!res.ok) {
      let doc: any;
      try {
        doc = await res.json();
      } catch {
        /* not JSON */
      }
      throw new ProvError(res.status, doc?.error?.code ?? `http_${res.status}`, doc?.error?.message ?? "");
    }
    return res;
  }

  private async json<T>(method: string, path: string, body?: unknown, cred?: Credential, etag?: string): Promise<T> {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (cred) headers.Authorization = `Bearer ${cred.device_id}.${cred.device_secret}`;
    if (etag) headers["If-None-Match"] = `"${etag}"`;
    let res: Response;
    try {
      res = await this.fetchFn(this.base + path, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        cache: "no-store",
        credentials: "omit",
      });
    } catch (e) {
      throw new ProvError(0, "unreachable", String(e));
    }
    if (res.status === 304) return null as T;
    const text = await res.text();
    let doc: any = undefined;
    try {
      doc = text ? JSON.parse(text) : undefined;
    } catch {
      /* not JSON */
    }
    if (!res.ok) {
      throw new ProvError(res.status, doc?.error?.code ?? `http_${res.status}`, doc?.error?.message ?? text.slice(0, 200));
    }
    return doc as T;
  }
}
